import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { fetchWithAuth } from '../utils/api';
import { sseClient, SseEvent } from '../utils/sse';
import { Subscription, Subject } from 'rxjs';
import { API_BASE_URL } from '../config/constants';
import { User, UserRole } from '../types/auth';
import { mapBackendRoleToFrontend } from '../utils/roleUtils';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  sseEvents?: Subject<SseEvent>;
  
  // 権限チェック関数
  isAdmin: () => boolean;
  canEditSchedules: () => boolean;
  canViewScheduleDetails: () => boolean;
}

// nullの代わりにデフォルト値を設定することで型エラーを防止
const defaultValue: AuthContextType = {
  user: null,
  loading: false,
  error: null,
  login: async () => { throw new Error('AuthContext not initialized'); },
  logout: () => { throw new Error('AuthContext not initialized'); },
  isAdmin: () => false,
  canEditSchedules: () => false,
  canViewScheduleDetails: () => false
};

const AuthContext = createContext<AuthContextType>(defaultValue);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sseSubscription, setSseSubscription] = useState<Subscription | null>(null);
  const [sseEvents] = useState(new Subject<SseEvent>());
  
  // SSE接続が初期化されたかどうかを追跡するRef
  const sseInitializedRef = useRef(false);
  
  // イベント数をカウントするデバッグ用のRef
  const eventCountRef = useRef(0);

  // SSE接続を確立 - ここでのみ接続処理を行う
  const connectToSse = useCallback(() => {
    // 既に接続済みなら実行しない
    if (sseInitializedRef.current) {
      console.log('SSE接続は既に確立されています - 処理をスキップします');
      return;
    }
    
    console.log('SSE接続を開始します');
    sseInitializedRef.current = true;
    
    if (sseSubscription) {
      console.log('既存のSSEサブスクリプションを解除します');
      sseSubscription.unsubscribe();
    }
    
    // SSEクライアントを初期化して接続
    const subscription = sseClient.connect().subscribe({
      next: (event: SseEvent) => {
        // イベントカウントをインクリメント（デバッグ用）
        eventCountRef.current++;
        
        // 接続成功イベントは1回だけログ出力
        if (event.type === 'connected') {
          console.log(`SSE接続成功 (イベント#${eventCountRef.current}):`, event.data);
        } else {
          console.log(`SSEイベント受信 (イベント#${eventCountRef.current}): ${event.type}`);
        }
        
        // イベントを転送
        sseEvents.next(event);
        
        // エラー処理
        if (event.type === 'error') {
          console.error('SSEエラー:', event.data);
          
          // 401エラーが発生した場合は自動的にログアウト
          if (event.data.message?.includes('認証エラー')) {
            logout();
            return;
          }
          
          // その他のエラーは必要に応じてユーザーに通知
          setError(event.data.message || 'サーバーとの接続エラーが発生しました');
        }
      },
      error: (err) => {
        console.error('SSEストリームエラー:', err);
        // エラーが発生した場合は接続状態をリセット
        sseInitializedRef.current = false;
      },
      complete: () => {
        console.log('SSEストリームが完了しました');
        // 接続が完了した場合も接続状態をリセット
        sseInitializedRef.current = false;
      }
    });
    
    setSseSubscription(subscription);
    
    // デバッグログ
    console.log('SSE接続サブスクリプションを設定しました');
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // JWTトークンを使ってユーザー情報を取得
  const fetchUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/me`);
      
      if (!response.ok) {
        if (response.status === 401) {
          // 認証エラー
          localStorage.removeItem('token');
          setUser(null);
          setError('セッションが期限切れです。再度ログインしてください。');
          return false;
        }
        throw new Error('ユーザー情報の取得に失敗しました');
      }
      
      const userData = await response.json();
      console.log('取得したユーザー情報:', userData);
      
      // 共通ユーティリティ関数を使用してロールをマッピング
      const frontendRole = mapBackendRoleToFrontend(userData.role);
      
      // ユーザーデータを適切な型に変換して設定
      setUser({
        ...userData,
        role: frontendRole
      });
      
      // ログインしている場合はSSE接続を確立
      connectToSse();
      
      return true;
    } catch (err) {
      console.error('ユーザー情報の取得に失敗:', err);
      setError('ユーザー情報の取得に失敗しました');
      return false;
    } finally {
      setLoading(false);
    }
  }, [connectToSse]);

  // アプリケーション終了時のクリーンアップ
  useEffect(() => {
    return () => {
      console.log('AuthProviderのクリーンアップを実行します');
      if (sseSubscription) {
        console.log('SSEサブスクリプションを解除します');
        sseSubscription.unsubscribe();
      }
      
      console.log('SSEクライアントを切断します');
      sseClient.disconnect();
      
      // 接続状態をリセット
      sseInitializedRef.current = false;
    };
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 初回ロード時にユーザー情報を取得
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }
        
        const response = await fetchWithAuth(`${API_BASE_URL}/auth/me`);
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          
          // ユーザー情報取得成功時にSSE接続を開始
          // SSE接続は一度だけ行われる
          console.log('checkAuthStatusで認証成功 - SSE接続を試みます');
          connectToSse();
        } else if (response.status === 401) {
          // 401エラーの場合はトークンを削除
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('認証確認エラー:', error);
      } finally {
        setLoading(false);
      }
    };

    console.log('初期認証チェックを実行します');
    checkAuthStatus();
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`${email}でログイン試行中...`);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      console.log('ログインレスポンス:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error('ログインに失敗しました');
      }

      const data = await response.json();
      console.log('トークンデータ:', data);
      localStorage.setItem('token', data.access_token);
      
      // デバッグのためにトークンを検証
      console.log('保存されたトークン:', localStorage.getItem('token'));
      
      // ログイン後にユーザー情報を取得
      console.log('/auth/me にリクエスト送信...');
      const userResponse = await fetchWithAuth(`${API_BASE_URL}/auth/me`);
      console.log('/auth/me レスポンス:', userResponse.status);
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('取得したユーザーデータ:', userData);
        
        // 共通ユーティリティ関数を使用してロールをマッピング
        const frontendRole = mapBackendRoleToFrontend(userData.role);
        
        // userデータを設定
        setUser({
          ...userData,
          role: frontendRole
        });
        
        console.log('setUserを実行、ロール:', frontendRole);
        
        // SSE接続を開始（一度だけ）
        console.log('loginでユーザー情報取得成功 - SSE接続を試みます');
        connectToSse();
        
        console.log('リダイレクト実行');
        window.location.href = '/';
      }
    } catch (err) {
      console.error('ログインエラー詳細:', err);
      setError('メールアドレスまたはパスワードが間違っています');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    console.log('ログアウト処理を実行します');
    
    // SSE接続を閉じる
    if (sseSubscription) {
      console.log('ログアウト時にSSEサブスクリプションを解除します');
      sseSubscription.unsubscribe();
      setSseSubscription(null);
    }
    
    console.log('SSEクライアントを切断します');
    sseClient.disconnect();
    
    // 接続状態をリセット
    sseInitializedRef.current = false;
    
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/login';
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 権限チェックヘルパー関数 - より強化
  const isAdmin = useCallback(() => {
    if (!user) return false;
    return user.role === 'admin';
  }, [user]);
  
  const canEditSchedules = useCallback(() => {
    if (!user) return false;
    return user.role === 'admin' || user.role === 'editor';
  }, [user]);
  
  const canViewScheduleDetails = useCallback(() => {
    if (!user) return false;
    return user.role === 'admin' || user.role === 'editor' || user.role === 'viewer';
  }, [user]);

  // より安全な型設定
  const contextValue: AuthContextType = {
    user,
    loading,
    error,
    login,
    logout,
    sseEvents, // これを追加
    isAdmin,
    canEditSchedules,
    canViewScheduleDetails
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};